import axios, {
	type AxiosRequestConfig,
	type RawAxiosRequestHeaders,
	type AxiosRequestHeaders,
} from "axios";

import Cookie from "../../packages/Cookie";
import env from "../../utilities/env";
import ID_TOKEN from "../id_token";

const v2Endpoint = env("REACT_APP_DCS_ENDPOINT");
const v3Endpoint = env("REACT_APP_DCS_V3_ENDPOINT");

const DCS3_XClient = env("REACT_APP_DCS_CLIENT");

const DCS3_ENDPOINT_MAP = {
	V2: v2Endpoint,
	V3: v3Endpoint,
} as const;

const useDCS3Api = (version: keyof typeof DCS3_ENDPOINT_MAP) => {
	const request = axios.create({
		baseURL: DCS3_ENDPOINT_MAP[version],
		headers: {
			"X-BRAND": "L",
			"X-CLIENT": DCS3_XClient,
			"X-VERSION": "1.0",
		},
	});

	request.interceptors.request.use(async (config) => {
		try {
			const [id_token] = Cookie("id_token");
			const [id_token_tmnab2cqa] = Cookie("id_token_tmnab2cqa");
			const [id_token_local] = Cookie("id_token_local");
			const auth =
				(process.env.NODE_ENV !== "production" ? id_token_local : undefined) ||
				id_token ||
				id_token_tmnab2cqa ||
				ID_TOKEN ||
				"";

			const headers = config.headers ?? ({} as AxiosRequestHeaders);
			headers.Authorization = auth;
			config.headers = headers;
			return config;
		} catch (err) {
			return Promise.reject(err);
		}
	});

	const makeHeaders = (
		headers?: RawAxiosRequestHeaders,
		data?: AxiosRequestConfig["data"],
	): AxiosRequestConfig => {
		return {
			...(data?.responseType ? { responseType: data.responseType } : {}),
			...(headers ? { headers } : {}),
		};
	};

	return {
		get: <T>(path: string, headers?: RawAxiosRequestHeaders) =>
			request.get<T>(path, makeHeaders(headers)),
		post: <T>(
			path: string,
			data?: AxiosRequestConfig["data"],
			headers?: RawAxiosRequestHeaders,
		) => request.post<T>(path, data, makeHeaders(headers, data)),
		patch: <T>(
			path: string,
			data?: AxiosRequestConfig["data"],
			headers?: RawAxiosRequestHeaders,
		) => request.patch<T>(path, data, makeHeaders(headers, data)),
		put: <T>(
			path: string,
			data?: AxiosRequestConfig["data"],
			headers?: RawAxiosRequestHeaders,
		) => request.put<T>(path, data, makeHeaders(headers, data)),
		delete: <T>(
			path: string,
			data?: AxiosRequestConfig["data"],
			headers?: RawAxiosRequestHeaders,
		) => request.delete<T>(path, { headers, data }),
	};
};

export default useDCS3Api;
